import type { JsonObject, JsonValue, ToolDef } from "@fabric-harness/sdk";
import type {
  CreateJobRequest,
  JobsClient,
  JobSettings,
  RunTaskSettings,
  SubmitRunRequest,
  TaskSettings,
} from "@databricks/sdk-jobs/v2";

export type DatabricksJobsAuthoringClient = Pick<
  JobsClient,
  "createJob" | "getJob" | "listJobs" | "updateJob" | "deleteJob" | "repair" | "submitRun"
>;

export interface DatabricksNotebookTaskSpec {
  notebookPath: string;
  baseParameters?: JsonObject;
  source?: "WORKSPACE" | "GIT";
}
export interface DatabricksSparkJarTaskSpec {
  mainClassName: string;
  parameters?: string[];
}
export interface DatabricksPythonWheelTaskSpec {
  packageName: string;
  entryPoint: string;
  parameters?: string[];
  namedParameters?: Record<string, string>;
}
export interface DatabricksDbtTaskSpec {
  commands: string[];
  warehouseId?: string;
  catalog?: string;
  schema?: string;
}
export interface DatabricksSqlTaskSpec {
  warehouseId: string;
  queryId?: string;
  filePath?: string;
  parameters?: Record<string, string>;
}
export interface DatabricksNewClusterSpec {
  policyId: string;
  sparkVersion: string;
  nodeTypeId: string;
  numWorkers: number;
  customTags?: Record<string, string>;
}

interface DatabricksTaskBase {
  taskKey: string;
  dependsOn?: string[];
  existingClusterId?: string;
  newCluster?: DatabricksNewClusterSpec;
}

export type DatabricksJobTask =
  | (DatabricksTaskBase & { notebook: DatabricksNotebookTaskSpec })
  | (DatabricksTaskBase & { sparkJar: DatabricksSparkJarTaskSpec })
  | (DatabricksTaskBase & { pythonWheel: DatabricksPythonWheelTaskSpec })
  | (DatabricksTaskBase & { dbt: DatabricksDbtTaskSpec })
  | (DatabricksTaskBase & { pipeline: { pipelineId: string; fullRefresh?: boolean } })
  | (DatabricksTaskBase & { sql: DatabricksSqlTaskSpec });

export interface DatabricksJobSchedule {
  quartzCronExpression: string;
  timezoneId: string;
  pauseStatus?: "PAUSED" | "UNPAUSED";
}
export interface DatabricksJobSpec {
  name: string;
  tasks: DatabricksJobTask[];
  description?: string;
  maxConcurrentRuns?: number;
  timeoutSeconds?: number;
  schedule?: DatabricksJobSchedule;
  tags?: Record<string, string>;
  serverlessPerformance?: "STANDARD" | "PERFORMANCE_OPTIMIZED";
}

export type DatabricksJobField =
  | "name"
  | "description"
  | "tasks"
  | "schedule"
  | "timeout_seconds"
  | "max_concurrent_runs"
  | "tags";
export interface DatabricksJobUpdate {
  set?: Partial<DatabricksJobSpec>;
  remove?: DatabricksJobField[];
}
export interface DatabricksComputePolicy {
  allowedPolicyIds?: string[];
  /** Existing all-purpose clusters are denied unless explicitly allowlisted. */
  allowedExistingClusterIds?: string[];
  maxWorkers?: number;
  maxConcurrentRuns?: number;
  maxRunTimeoutSeconds?: number;
  allowedNodeTypes?: string[];
  allowedRuntimeVersions?: string[];
  requiredTags?: Record<string, string>;
  schedules?: boolean;
  serverlessPerformance?: "STANDARD" | "PERFORMANCE_OPTIMIZED";
}

export function validateDatabricksComputePolicy(policy: DatabricksComputePolicy): void {
  for (const [name, value] of [
    ["maxWorkers", policy.maxWorkers],
    ["maxConcurrentRuns", policy.maxConcurrentRuns],
    ["maxRunTimeoutSeconds", policy.maxRunTimeoutSeconds],
  ] as const) {
    if (value !== undefined && (!Number.isFinite(value) || value < 0))
      throw new Error(`databricks jobs: computePolicy.${name} must be a non-negative number.`);
  }
  for (const [name, values] of [
    ["allowedPolicyIds", policy.allowedPolicyIds],
    ["allowedExistingClusterIds", policy.allowedExistingClusterIds],
    ["allowedNodeTypes", policy.allowedNodeTypes],
    ["allowedRuntimeVersions", policy.allowedRuntimeVersions],
  ] as const) {
    if (values !== undefined && values.length === 0)
      throw new Error(`databricks jobs: computePolicy.${name} must be non-empty when configured.`);
  }
  for (const [key, value] of Object.entries(policy.requiredTags ?? {}))
    if (!key || !value)
      throw new Error(
        "databricks jobs: computePolicy.requiredTags cannot contain empty keys or values.",
      );
}

export interface DatabricksCreateJobOptions {
  ifExists?: "error" | "reuse";
}
export interface DatabricksCreatedJob {
  jobId: number;
  reused: boolean;
  fingerprint: string;
}

const MANAGED_TAG = "fabric-harness:managed";
const FINGERPRINT_TAG = "fabric-harness:fingerprint";

export class DatabricksJobsAuthoring {
  constructor(
    private readonly client: DatabricksJobsAuthoringClient,
    private readonly computePolicy: DatabricksComputePolicy = {},
  ) {
    validateDatabricksComputePolicy(computePolicy);
  }

  async createJob(
    spec: DatabricksJobSpec,
    options: DatabricksCreateJobOptions = {},
  ): Promise<DatabricksCreatedJob> {
    validateJobSpec(spec, this.computePolicy);
    const fingerprint = await jobFingerprint(spec);
    if (options.ifExists === "reuse") {
      const listing = await this.listJobs({ name: spec.name, expandTasks: true });
      const matches = listing.jobs.filter((job) => jobName(job) === spec.name);
      const managed = matches.filter((job) => {
        const tags = jobTags(job);
        return tags[MANAGED_TAG] === "true" && tags[FINGERPRINT_TAG] === fingerprint;
      });
      const reusable = managed[0];
      if (managed.length === 1 && reusable)
        return { jobId: jobId(reusable), reused: true, fingerprint };
      if (matches.length > 0)
        throw new Error(
          `databricks jobs: refusing to reuse "${spec.name}" because ownership or fingerprint is ambiguous.`,
        );
    }
    const body = serializeJobSpec({
      ...spec,
      tags: { ...(spec.tags ?? {}), [MANAGED_TAG]: "true", [FINGERPRINT_TAG]: fingerprint },
    });
    const response = await this.client.createJob(body);
    if (response.jobId === undefined)
      throw new Error(
        "Databricks Jobs create response did not contain job_id. The create was not retried; inspect by name and fingerprint before retrying.",
      );
    return { jobId: safeNumber(response.jobId, "job_id"), reused: false, fingerprint };
  }

  async getJob(jobIdValue: number): Promise<Record<string, unknown>> {
    return plainRecord(await this.client.getJob({ jobId: BigInt(jobIdValue) }));
  }

  async listJobs(
    options: { name?: string; limit?: number; pageToken?: string; expandTasks?: boolean } = {},
  ): Promise<{ jobs: Record<string, unknown>[]; nextPageToken?: string }> {
    const response = await this.client.listJobs({
      ...(options.name ? { name: options.name } : {}),
      ...(options.limit !== undefined ? { limit: options.limit } : {}),
      ...(options.pageToken ? { pageToken: options.pageToken } : {}),
      ...(options.expandTasks !== undefined ? { expandTasks: options.expandTasks } : {}),
    });
    return {
      jobs: (response.jobs ?? []).map(plainRecord),
      ...(response.nextPageToken ? { nextPageToken: response.nextPageToken } : {}),
    };
  }

  async updateJob(jobIdValue: number, update: DatabricksJobUpdate): Promise<void> {
    if (!update.set && !update.remove?.length)
      throw new Error("databricks jobs: update requires set or remove.");
    if (update.set) validateJobUpdate(update.set, this.computePolicy);
    if (update.remove?.includes("tags"))
      throw new Error("databricks jobs: ownership tags cannot be removed.");
    const current = await this.getJob(jobIdValue);
    const settings = asRecord(current.settings);
    const serializedSet = update.set ? serializeJobSpec(update.set) : {};
    const serializedSetRecord = asRecord(serializedSet);
    const merged = mergeJobSettings(settings, serializedSetRecord, update.remove ?? []);
    const currentTags = stringRecord(settings.tags);
    const managed = currentTags[MANAGED_TAG] === "true";
    const protectedTags = managed
      ? {
          ...currentTags,
          ...stringRecord(serializedSetRecord.tags),
          [MANAGED_TAG]: "true",
          [FINGERPRINT_TAG]: await jobFingerprintFromSettings(merged),
        }
      : undefined;
    await this.client.updateJob({
        jobId: BigInt(jobIdValue),
        ...(update.set || protectedTags
          ? {
              newSettings: { ...serializedSet, ...(protectedTags ? { tags: protectedTags } : {}) },
            }
          : {}),
        ...(update.remove?.length ? { fieldsToRemove: update.remove.map(toSdkField) } : {}),
      });
  }

  async deleteJob(jobIdValue: number): Promise<void> {
    await this.client.deleteJob({ jobId: BigInt(jobIdValue) });
  }

  async repairRun(runId: number, rerunTasks?: string[]): Promise<{ repairId?: number }> {
    await this.client.repair({
      runId: BigInt(runId),
      rerunAllFailedTasks: !rerunTasks?.length,
      ...(rerunTasks?.length ? { rerunTasks } : {}),
    });
    return {};
  }

  async submitRun(
    spec: Omit<DatabricksJobSpec, "name"> & { runName: string; idempotencyToken?: string },
  ): Promise<{ runId: number }> {
    validateJobSpec({ ...spec, name: spec.runName }, this.computePolicy);
    const request: SubmitRunRequest = {
      runName: spec.runName,
      idempotencyToken: spec.idempotencyToken ?? crypto.randomUUID(),
      ...serializeRunSpec(spec),
    };
    const waiter = await this.client.submitRun(request);
    if (waiter.runId === undefined)
      throw new Error("Databricks Jobs submit response did not contain run_id.");
    return { runId: safeNumber(waiter.runId, "run_id") };
  }
}

export function databricksJobsAuthoring(
  client: DatabricksJobsAuthoringClient,
  computePolicy: DatabricksComputePolicy = {},
): DatabricksJobsAuthoring {
  return new DatabricksJobsAuthoring(client, computePolicy);
}

export function databricksJobsAuthoringTools(
  client: DatabricksJobsAuthoringClient,
  computePolicy: DatabricksComputePolicy = {},
  options: { oneOffCompute?: boolean } = {},
): ToolDef[] {
  const jobs = databricksJobsAuthoring(client, computePolicy);
  const tools: ToolDef[] = [
    authoringTool(
      "databricks_create_job",
      "Create a policy-bounded Databricks job.",
      jobSpecSchema(),
      [{ kind: "job", path: "/name" }, ...taskResources("/tasks")],
      (input) => {
        const ifExists = (input as { ifExists?: "error" | "reuse" }).ifExists;
        return jobs.createJob(input as unknown as DatabricksJobSpec, {
          ...(ifExists ? { ifExists } : {}),
        });
      },
    ),
    authoringTool(
      "databricks_update_job",
      "Update explicit Databricks job fields.",
      updateSchema(),
      [{ kind: "job", path: "/jobId" }, ...taskResources("/set/tasks", true)],
      (input) => {
        const set = (input as { set?: Partial<DatabricksJobSpec> }).set;
        const remove = (input as { remove?: DatabricksJobField[] }).remove;
        return jobs.updateJob((input as { jobId: number }).jobId, {
          ...(set ? { set } : {}),
          ...(remove ? { remove } : {}),
        });
      },
    ),
    authoringTool(
      "databricks_delete_job",
      "Delete one Databricks job by id.",
      idSchema("jobId"),
      [{ kind: "job", path: "/jobId" }],
      (input) => jobs.deleteJob((input as { jobId: number }).jobId),
    ),
    readTool("databricks_list_jobs", "List Databricks jobs.", listSchema(), (input) =>
      jobs.listJobs(
        input as { name?: string; limit?: number; pageToken?: string; expandTasks?: boolean },
      ),
    ),
    executeTool(
      "databricks_repair_run",
      "Repair failed tasks in a Databricks run.",
      repairSchema(),
      [{ kind: "run", path: "/runId" }],
      (input) =>
        jobs.repairRun(
          (input as { runId: number }).runId,
          (input as { rerunTasks?: string[] }).rerunTasks,
        ),
    ),
  ];
  if (options.oneOffCompute)
    tools.push(
      executeTool(
        "databricks_submit_run",
        "Submit a policy-bounded one-off multi-task run.",
        submitSchema(),
        taskResources("/tasks"),
        (input) => jobs.submitRun(input as never),
      ),
    );
  return tools;
}

function authoringTool(
  name: string,
  description: string,
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description,
    inputSchema,
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "jobs",
      governance: { resources },
    },
    execute,
  };
}
function executeTool(
  name: string,
  description: string,
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description,
    inputSchema,
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "jobs",
      governance: { resources },
    },
    execute,
  };
}
function readTool(
  name: string,
  description: string,
  inputSchema: JsonObject,
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description,
    inputSchema,
    metadata: { effect: "read", provider: "databricks", service: "jobs" },
    execute,
  };
}

function validateJobUpdate(
  spec: Partial<DatabricksJobSpec>,
  policy: DatabricksComputePolicy,
): void {
  if (spec.tasks) validateTasks(spec.tasks, policy);
  validateLimits(spec, policy);
}

export function validateJobSpec(spec: DatabricksJobSpec, policy: DatabricksComputePolicy): void {
  if (!spec.name?.trim()) throw new Error("databricks jobs: name is required.");
  if (!spec.tasks?.length) throw new Error("databricks jobs: at least one task is required.");
  validateTasks(spec.tasks, policy);
  validateLimits(spec, policy);
}

function validateLimits(spec: Partial<DatabricksJobSpec>, policy: DatabricksComputePolicy): void {
  if (spec.schedule && policy.schedules !== true)
    throw new Error("databricks jobs: schedules are disabled by computePolicy.");
  if (
    policy.maxConcurrentRuns !== undefined &&
    (spec.maxConcurrentRuns ?? 1) > policy.maxConcurrentRuns
  )
    throw new Error(`databricks jobs: maxConcurrentRuns exceeds ${policy.maxConcurrentRuns}.`);
  if (
    policy.maxRunTimeoutSeconds !== undefined &&
    (spec.timeoutSeconds ?? 0) > policy.maxRunTimeoutSeconds
  )
    throw new Error(`databricks jobs: timeoutSeconds exceeds ${policy.maxRunTimeoutSeconds}.`);
  if (
    spec.serverlessPerformance &&
    policy.serverlessPerformance &&
    spec.serverlessPerformance !== policy.serverlessPerformance
  )
    throw new Error(
      `databricks jobs: serverlessPerformance must be ${policy.serverlessPerformance}.`,
    );
}

function validateTasks(tasks: DatabricksJobTask[], policy: DatabricksComputePolicy): void {
  const keys = new Set<string>();
  for (const task of tasks) {
    if (!task.taskKey || keys.has(task.taskKey))
      throw new Error(`databricks jobs: taskKey must be non-empty and unique (${task.taskKey}).`);
    keys.add(task.taskKey);
    if (task.existingClusterId && task.newCluster)
      throw new Error(
        `databricks jobs: task ${task.taskKey} cannot use existingClusterId and newCluster together.`,
      );
    if (task.existingClusterId) {
      if (!policy.allowedExistingClusterIds?.includes(task.existingClusterId))
        throw new Error(
          `databricks jobs: existing cluster ${task.existingClusterId} is not in computePolicy.allowedExistingClusterIds.`,
        );
      continue;
    }
    if (!task.newCluster) continue;
    const cluster = task.newCluster;
    if (!policy.allowedPolicyIds?.length)
      throw new Error(
        "databricks jobs: classic compute requires a non-empty computePolicy.allowedPolicyIds.",
      );
    if (!policy.allowedPolicyIds.includes(cluster.policyId))
      throw new Error(`databricks jobs: cluster policy ${cluster.policyId} is not allowed.`);
    if (policy.maxWorkers !== undefined && cluster.numWorkers > policy.maxWorkers)
      throw new Error(`databricks jobs: numWorkers exceeds ${policy.maxWorkers}.`);
    if (policy.allowedNodeTypes && !policy.allowedNodeTypes.includes(cluster.nodeTypeId))
      throw new Error(`databricks jobs: node type ${cluster.nodeTypeId} is not allowed.`);
    if (
      policy.allowedRuntimeVersions &&
      !policy.allowedRuntimeVersions.includes(cluster.sparkVersion)
    )
      throw new Error(`databricks jobs: runtime ${cluster.sparkVersion} is not allowed.`);
    for (const [key, value] of Object.entries(policy.requiredTags ?? {}))
      if (cluster.customTags?.[key] !== value)
        throw new Error(`databricks jobs: cluster tag ${key}=${value} is required.`);
  }
}

function serializeJobSpec(spec: Partial<DatabricksJobSpec>): CreateJobRequest & JobSettings {
  return {
    ...("name" in spec && spec.name ? { name: spec.name } : {}),
    ...(spec.description ? { description: spec.description } : {}),
    ...(spec.tasks ? { tasks: spec.tasks.map(serializeTask) } : {}),
    ...(spec.maxConcurrentRuns !== undefined
      ? { maxConcurrentRuns: spec.maxConcurrentRuns }
      : {}),
    ...(spec.timeoutSeconds !== undefined ? { timeoutSeconds: spec.timeoutSeconds } : {}),
    ...(spec.schedule
      ? {
          schedule: {
            quartzCronExpression: spec.schedule.quartzCronExpression,
            timezoneId: spec.schedule.timezoneId,
            ...(spec.schedule.pauseStatus ? { pauseStatus: spec.schedule.pauseStatus } : {}),
          },
        }
      : {}),
    ...(spec.tags ? { tags: spec.tags } : {}),
    ...(spec.serverlessPerformance ? { performanceTarget: spec.serverlessPerformance } : {}),
  };
}

function serializeRunSpec(
  spec: Omit<DatabricksJobSpec, "name">,
): Partial<Pick<SubmitRunRequest, "tasks" | "timeoutSeconds">> {
  return {
    ...(spec.tasks ? { tasks: spec.tasks.map(serializeRunTask) } : {}),
    ...(spec.timeoutSeconds !== undefined ? { timeoutSeconds: spec.timeoutSeconds } : {}),
  };
}

function serializeTask(task: DatabricksJobTask): TaskSettings {
  const base = {
    taskKey: task.taskKey,
    ...(task.dependsOn ? { dependsOn: task.dependsOn.map((taskKey) => ({ taskKey })) } : {}),
    ...(task.existingClusterId
      ? { spec: { $case: "existingClusterId" as const, existingClusterId: task.existingClusterId } }
      : {}),
    ...(task.newCluster
      ? {
          spec: { $case: "newCluster" as const, newCluster: {
            policyId: task.newCluster.policyId,
            sparkVersion: task.newCluster.sparkVersion,
            nodeTypeId: task.newCluster.nodeTypeId,
            size: {
              $case: "numWorkers" as const,
              numWorkers: task.newCluster.numWorkers,
            },
            ...(task.newCluster.customTags ? { customTags: task.newCluster.customTags } : {}),
          } },
        }
      : {}),
  };
  if ("notebook" in task)
    return {
      ...base,
      task: { $case: "notebookTask", notebookTask: {
        notebookPath: task.notebook.notebookPath,
        source: task.notebook.source ?? "WORKSPACE",
        ...(task.notebook.baseParameters
          ? { baseParameters: stringValues(task.notebook.baseParameters) }
          : {}),
      } },
    };
  if ("sparkJar" in task)
    return {
      ...base,
      task: { $case: "sparkJarTask", sparkJarTask: {
        mainClassName: task.sparkJar.mainClassName,
        ...(task.sparkJar.parameters ? { parameters: task.sparkJar.parameters } : {}),
      } },
    };
  if ("pythonWheel" in task)
    return {
      ...base,
      task: { $case: "pythonWheelTask", pythonWheelTask: {
        packageName: task.pythonWheel.packageName,
        entryPoint: task.pythonWheel.entryPoint,
        ...(task.pythonWheel.parameters ? { parameters: task.pythonWheel.parameters } : {}),
        ...(task.pythonWheel.namedParameters
          ? { namedParameters: task.pythonWheel.namedParameters }
          : {}),
      } },
    };
  if ("dbt" in task)
    return {
      ...base,
      task: { $case: "dbtTask", dbtTask: {
        commands: task.dbt.commands,
        ...(task.dbt.warehouseId ? { warehouseId: task.dbt.warehouseId } : {}),
        ...(task.dbt.catalog ? { catalog: task.dbt.catalog } : {}),
        ...(task.dbt.schema ? { schema: task.dbt.schema } : {}),
      } },
    };
  if ("pipeline" in task)
    return {
      ...base,
      task: { $case: "pipelineTask", pipelineTask: {
        pipelineId: task.pipeline.pipelineId,
        ...(task.pipeline.fullRefresh !== undefined
          ? { fullRefresh: task.pipeline.fullRefresh }
          : {}),
      } },
    };
  return {
    ...base,
    task: { $case: "sqlTask", sqlTask: {
      warehouseId: task.sql.warehouseId,
      ...(task.sql.queryId
        ? { sqlTaskType: { $case: "query" as const, query: { queryType: { $case: "queryId" as const, queryId: task.sql.queryId } } } }
        : {}),
      ...(task.sql.filePath
        ? { sqlTaskType: { $case: "file" as const, file: { path: task.sql.filePath } } }
        : {}),
      ...(task.sql.parameters ? { parameters: task.sql.parameters } : {}),
    } },
  };
}

function serializeRunTask(task: DatabricksJobTask): RunTaskSettings {
  return serializeTask(task) as RunTaskSettings;
}

async function jobFingerprint(spec: DatabricksJobSpec): Promise<string> {
  return jobFingerprintFromSettings(serializeJobSpec(spec) as unknown as JsonObject);
}
async function jobFingerprintFromSettings(settings: JsonObject): Promise<string> {
  const tags = stringRecord(settings.tags);
  const normalized = stableJson({
    ...settings,
    ...(settings.tags
      ? {
          tags: Object.fromEntries(
            Object.entries(tags).filter(([key]) => key !== MANAGED_TAG && key !== FINGERPRINT_TAG),
          ),
        }
      : {}),
  });
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
function asRecord(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {};
}
function stringRecord(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}
function mergeJobSettings(
  current: JsonObject,
  update: JsonObject,
  remove: DatabricksJobField[],
): JsonObject {
  const merged: JsonObject = { ...current, ...update };
  if (Array.isArray(current.tasks) && Array.isArray(update.tasks)) {
    const byKey = new Map<string, JsonValue>();
    for (const task of current.tasks) {
      const key = asRecord(task).taskKey;
      if (typeof key === "string") byKey.set(key, task);
    }
    for (const task of update.tasks) {
      const key = asRecord(task).taskKey;
      if (typeof key === "string") byKey.set(key, task);
    }
    merged.tasks = [...byKey.values()];
  }
  for (const field of remove) delete merged[toSdkField(field)];
  return merged;
}
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, child]) => `${JSON.stringify(key)}:${stableJson(child)}`)
      .join(",")}}`;
  return JSON.stringify(value);
}
function jobName(job: Record<string, unknown>): string | undefined {
  const settings = job.settings as Record<string, unknown> | undefined;
  return typeof settings?.name === "string" ? settings.name : undefined;
}
function jobTags(job: Record<string, unknown>): Record<string, string> {
  const settings = job.settings as Record<string, unknown> | undefined;
  const tags = settings?.tags;
  return tags && typeof tags === "object" && !Array.isArray(tags)
    ? (tags as Record<string, string>)
    : {};
}
function jobId(job: Record<string, unknown>): number {
  if (!Number.isSafeInteger(job.jobId))
    throw new Error("databricks jobs: matching job has no job_id.");
  return job.jobId as number;
}

function toSdkField(field: DatabricksJobField): string {
  if (field === "timeout_seconds") return "timeoutSeconds";
  if (field === "max_concurrent_runs") return "maxConcurrentRuns";
  return field;
}

function safeNumber(value: bigint, field: string): number {
  const number = Number(value);
  if (!Number.isSafeInteger(number))
    throw new Error(`Databricks Jobs ${field} exceeded JavaScript's safe integer range.`);
  return number;
}

function stringValues(value: JsonObject): Record<string, string> {
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      typeof item === "string" ? item : JSON.stringify(item),
    ]),
  );
}

function plainRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return plainValue(value) as Record<string, unknown>;
}

function plainValue(value: unknown): unknown {
  if (typeof value === "bigint") return safeNumber(value, "response identifier");
  if (Array.isArray(value)) return value.map(plainValue);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, plainValue(item)]),
    );
  return value;
}

function taskResources(prefix: string, optional = false): JsonObject[] {
  return [
    { kind: "notebook", path: `${prefix}/*/notebook/notebookPath`, optional: true },
    { kind: "pipeline", path: `${prefix}/*/pipeline/pipelineId`, optional: true },
    { kind: "catalog", path: `${prefix}/*/dbt/catalog`, catalogQualified: true, optional: true },
  ];
}
function idSchema(key: string): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { [key]: { type: "number" } },
    required: [key],
  };
}
function listSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: { type: "string" },
      limit: { type: "number" },
      pageToken: { type: "string" },
      expandTasks: { type: "boolean" },
    },
  };
}
function repairSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      runId: { type: "number" },
      rerunTasks: { type: "array", items: { type: "string" } },
    },
    required: ["runId"],
  };
}
function taskSchema(): JsonObject {
  const base = {
    taskKey: { type: "string" },
    dependsOn: { type: "array", items: { type: "string" } },
    existingClusterId: { type: "string" },
    newCluster: {
      type: "object",
      properties: {
        policyId: { type: "string" },
        sparkVersion: { type: "string" },
        nodeTypeId: { type: "string" },
        numWorkers: { type: "number" },
        customTags: { type: "object", additionalProperties: { type: "string" } },
      },
      required: ["policyId", "sparkVersion", "nodeTypeId", "numWorkers"],
    },
  };
  const variant = (key: string, properties: JsonObject, required: string[]) => ({
    type: "object",
    additionalProperties: false,
    properties: {
      ...base,
      [key]: { type: "object", additionalProperties: false, properties, required },
    },
    required: ["taskKey", key],
  });
  return {
    oneOf: [
      variant(
        "notebook",
        {
          notebookPath: { type: "string" },
          baseParameters: { type: "object", additionalProperties: true },
          source: { type: "string" },
        },
        ["notebookPath"],
      ),
      variant(
        "sparkJar",
        {
          mainClassName: { type: "string" },
          parameters: { type: "array", items: { type: "string" } },
        },
        ["mainClassName"],
      ),
      variant(
        "pythonWheel",
        {
          packageName: { type: "string" },
          entryPoint: { type: "string" },
          parameters: { type: "array", items: { type: "string" } },
          namedParameters: { type: "object", additionalProperties: { type: "string" } },
        },
        ["packageName", "entryPoint"],
      ),
      variant(
        "dbt",
        {
          commands: { type: "array", items: { type: "string" } },
          warehouseId: { type: "string" },
          catalog: { type: "string" },
          schema: { type: "string" },
        },
        ["commands"],
      ),
      variant("pipeline", { pipelineId: { type: "string" }, fullRefresh: { type: "boolean" } }, [
        "pipelineId",
      ]),
      variant(
        "sql",
        {
          warehouseId: { type: "string" },
          queryId: { type: "string" },
          filePath: { type: "string" },
          parameters: { type: "object", additionalProperties: { type: "string" } },
        },
        ["warehouseId"],
      ),
    ],
  } as JsonObject;
}
function specProperties(): JsonObject {
  return {
    name: { type: "string" },
    description: { type: "string" },
    tasks: { type: "array", items: taskSchema() },
    maxConcurrentRuns: { type: "number" },
    timeoutSeconds: { type: "number" },
    schedule: {
      type: "object",
      properties: {
        quartzCronExpression: { type: "string" },
        timezoneId: { type: "string" },
        pauseStatus: { type: "string" },
      },
      required: ["quartzCronExpression", "timezoneId"],
    },
    tags: { type: "object", additionalProperties: { type: "string" } },
    serverlessPerformance: { type: "string", enum: ["STANDARD", "PERFORMANCE_OPTIMIZED"] },
  };
}
function jobSpecSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { ...specProperties(), ifExists: { type: "string", enum: ["error", "reuse"] } },
    required: ["name", "tasks"],
  };
}
function updateSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      jobId: { type: "number" },
      set: { type: "object", additionalProperties: false, properties: specProperties() },
      remove: { type: "array", items: { type: "string" } },
    },
    required: ["jobId"],
  };
}
function submitSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      ...specProperties(),
      runName: { type: "string" },
      idempotencyToken: { type: "string" },
    },
    required: ["runName", "tasks"],
  };
}
